<template>
  <v-card
    :width="cardWidth"
    :height="cardHeight"
    elevation="1"
    @dragenter.stop="handleDragEnter"
    @dragover.stop="handleDragEnter"
    @dragleave="handleDragLeave"
    @dragend="handleDragLeave"
    @click="handleSpotClick"
    :data-testid="`yard-spot-${spot.code}`"
    :draggable="false"
    :id="spot.id"
    :class="{
      'drag-active': isDragActive,
      'drag-disabled': isDragDisabled,
      'bg--color-success-20': isSpotOpen(spot),
      'disabled-background': isSpotClosed(spot),
      'drop-zone': !isSpotClosed(spot),
      'spot-selected': isSpotSelected
    }"
    class="spot-title">
    <v-card-title
      class="pa-1"
      :class="{
        'bg--color-neutral-20': !selectedSpot || !isSpotSelected,
        'bg--color-primary-60': isSpotSelected
      }">
      <div
        class="font-size-small text-center full-width text-uppercase spot-card-code"
        :data-testid="`yard-spot-title-${spot.code}`">
        <strong>{{ spot.code }}</strong>
      </div>
    </v-card-title>

    <v-card-text class="card-content">
      <div
        v-if="isLoading"
        class="d-flex justify-center align-center"
        :style="{ height: cardContentHeight + 'px' }">
        <v-progress-circular color="primary" indeterminate size="20"></v-progress-circular>
      </div>
      <v-card
        v-else
        elevation="0"
        :draggable="isSpotAssigned(spot)"
        :id="spotAssignment?.id"
        @dragstart="onDragStart"
        @dragend="onDragEnd"
        :class="{ dragging: isDragging }"
        :height="cardContentHeight"
        class="d-flex flex-column align-center justify-center text-center bg--color-background-transparent">
        <yard-view-spot-unassigned :spot="spot" v-if="!isSpotAssigned(spot)" />
        <div v-else :data-testid="spotTestId">
          <yard-view-spot-assigned
            :spot="spot"
            :appointment="appointment"
            :is-dragging="isDragging" />
        </div>
      </v-card>
    </v-card-text>
  </v-card>
</template>

<script>
import { computed, defineComponent, onBeforeMount, onBeforeUnmount, ref } from 'vue';
import useYard from '@/modules/yard/composables/useYard';

import YardViewSpotUnassigned from '@/modules/yard/components/Spot/Card/YardViewSpotUnassigned.vue';
import YardViewSpotAssigned from '@/modules/yard/components/Spot/Card/YardViewSpotAssigned.vue';
import { useEventHub, useSockets, useNotify } from '@/composables';
import { isUUID } from 'class-validator';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewSpot',
  components: { YardViewSpotAssigned, YardViewSpotUnassigned },
  props: {
    spot: {
      type: Object,
      required: true
    }
  },
  setup(props) {
    const { t } = useI18n();
    const yardStore = useYard();
    const eventHub = useEventHub();
    const sockets = useSockets();
    const notify = useNotify();

    const isLoading = ref(false);

    const isDragActive = ref(false);
    const isDragDisabled = ref(false);

    const isDragging = ref(false);
    const originalElement = ref(null);

    const handleSpotClick = () => {
      if (yardStore.selectedSpotRef.value?.id === props.spot.id) {
        return;
      }
      yardStore.selectedSpotRef.value = props.spot;
    };

    const spotAssignment = computed(() => props.spot.spotAssignments[0]);
    const appointment = computed(() => spotAssignment.value?.assetVisit?.appointment);
    const assetVisit = computed(() => spotAssignment.value?.assetVisit);
    const assetContainer = computed(() => spotAssignment.value?.assetContainer);

    const spotTestId = computed(() => {
      let testId = `spot-assigned`;

      if (assetVisit.value) {
        testId += `-visit-${assetVisit.value.id}-type-${assetVisit.value.visitType}`;
      }

      if (assetContainer.value) {
        testId += `-container-${assetContainer.value.id}`;
      }

      return testId;
    });

    const isSpotSelected = computed(() => yardStore.selectedSpotRef.value?.id === props.spot.id);

    const handleDragEnter = () => {
      if (!yardStore.isSpotClosed(props.spot)) {
        isDragActive.value = true;
      } else {
        isDragDisabled.value = true;
      }
    };

    const handleDragLeave = () => {
      isDragActive.value = false;
      isDragDisabled.value = false;
    };

    const updateAppointmentFromSubspace = event => {
      if (event.id === appointment?.value?.id && props.spot.spotAssignments.length) {
        props.spot.spotAssignments[0].assetVisit.appointment = event;
      }
    };

    const onDragStart = event => {
      isDragging.value = true;
      originalElement.value = event.target;

      // Set the data for transfer
      event.dataTransfer.setData('text/plain', event.target.id);
    };

    const onDragEnd = async event => {
      isLoading.value = true;
      // Check if the element was dropped inside a drop-zone

      const newSpotElement = document
        .elementFromPoint(event.clientX, event.clientY)
        .closest('.drop-zone');

      isDragActive.value = false;
      isDragDisabled.value = false;

      // TODO: Abstract this
      if (isUUID(newSpotElement?.id)) {
        const spotAssignmentElement = event.srcElement;

        const oldSpotElement = spotAssignmentElement.parentElement.parentElement;

        const oldSpot = yardStore.spotsRef.value.find(spot => spot.id === oldSpotElement.id);
        const newSpot = yardStore.spotsRef.value.find(spot => spot.id === newSpotElement.id);

        const areSpotsValid = oldSpot?.id && newSpot?.id && oldSpot.id !== newSpot.id;

        if (areSpotsValid && spotAssignmentElement && !yardStore.isSpotClosed(newSpot)) {
          if (
            (yardStore.hasAssetVisitOnlyOnSpot(newSpot) &&
              !yardStore.hasAssetContainerOnlyOnSpot(oldSpot) &&
              !yardStore.isSpotOpen(newSpot)) ||
            (yardStore.hasAssetVisitOnSpot(newSpot) && yardStore.hasAssetVisitOnSpot(oldSpot)) ||
            (yardStore.hasAssetContainerOnSpot(newSpot) &&
              yardStore.hasAssetContainerOnSpot(oldSpot))
          ) {
            isDragging.value = false;
            isLoading.value = false;
            notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
            return;
          }

          const spotAssignment = await yardStore.reassignSpotAssignment(spotAssignmentElement.id, {
            spotId: newSpot.id,
            oldSpotId: oldSpot.id,
            reason: 'Drag and Drop'
          });

          eventHub.$emit(yardStore.SPOT_ASSIGNMENT_CHANGED, {
            assetVisit: spotAssignment.assetVisit ?? spotAssignment.assetContainer,
            newSpotCode: spotAssignment.spot.code,
            newSpotAreaName: spotAssignment.spot.spotArea.name
          });

          yardStore.trackMixpanelEvent(yardStore.MixPanelEvent.REASSIGN, {
            spot: { ...newSpot, spotAssignments: { ...oldSpot.spotAssignments[0] } },
            entryPoint: 'Drag and Drop',
            assetVisit:
              newSpot.spotAssignments[0]?.assetVisit ?? newSpot.spotAssignments[0]?.assetContainer,
            metadata: {
              old_spot_id: oldSpot.id,
              old_spot_code: oldSpot.code
            }
          });
        } else {
          notify(t('yard.messages.invalidDestinationTryAgainToAnotherSpot'), 'warning');
        }
      } else {
        notify(t('yard.messages.invalidDestinationItMustBeAValidSpot'), 'warning');
      }
      isDragging.value = false;
      isLoading.value = false;
    };

    onBeforeMount(() =>
      Object.keys(sockets.actions).forEach(operation =>
        eventHub.$on(`${operation}-Appointment`, updateAppointmentFromSubspace)
      )
    );

    onBeforeUnmount(() =>
      Object.keys(sockets.actions).forEach(operation =>
        eventHub.$off(`${operation}-Appointment`, updateAppointmentFromSubspace)
      )
    );

    return {
      isLoading,
      selectedWarehouse: yardStore.selectedWarehouseRef,
      selectedSpot: yardStore.selectedSpotRef,
      handleSpotClick,
      handleDragEnter,
      handleDragLeave,
      isSpotAssigned: yardStore.isSpotAssigned,
      isSpotClosed: yardStore.isSpotClosed,
      isSpotOpen: yardStore.isSpotOpen,
      hasAssetVisitOnSpot: yardStore.hasAssetVisitOnSpot,
      spotAssignment,
      appointment,
      isDragActive,
      isDragDisabled,
      isSpotSelected,
      isDragging,
      onDragStart,
      onDragEnd,
      spotTestId,
      spotStatus: yardStore.spotStatus,
      getSpotStatus: yardStore.getSpotStatus,
      getSpotIcon: yardStore.getSpotIcon,
      cardWidth: yardStore.spotCardWidth,
      cardHeight: yardStore.spotCardHeight,
      cardContentHeight: yardStore.spotCardHeight - 45
    };
  }
});
</script>
<style lang="scss" scoped>
.spot-title {
  border: 1px solid $color-neutral-60 !important;
  &:hover {
    border-color: $color-primary-60 !important;
  }
  user-select: none !important;
}
.spot-selected {
  border: 1px solid $color-primary-60 !important;
}
.drag-active {
  border: 3px solid $color-primary-60 !important;
  opacity: 0.7 !important;
}
.drag-disabled {
  opacity: 0.3 !important;
  background-color: lightgrey !important;
}
.dragging {
  opacity: 0.3 !important;
}
.card-content {
  border-top: 4px solid $white !important;
  padding: $spacing-1;
}
.disabled-background {
  width: 100%;
  height: 100%;
  background: repeating-linear-gradient(
    30deg,
    $white,
    $white 4px,
    $color-neutral-20 4px,
    $color-neutral-20 8px
  );
}
</style>
